import { useEffect, useState } from 'react';
import { Box, Grid, LinearProgress, Paper, Snackbar, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

//import { UsuariosService } from '../../shared/services/api/usuario/UsuariosService';
import { VTextField, VForm, useVForm, IVFormErrors } from '../../shared/forms';
import { FerramentasDeDetalhe } from '../../shared/components';
import { LayoutBaseDePagina } from '../../shared/layouts';
import { RoleCheckbox } from './components/ListRolesComponent';
import { UsuariosServiceAPI } from '../../shared/services/api/usuario/UsuariosService';
import { useAuth } from '../../shared/contexts';
import { IFormData, IRole } from '../../shared/types/types';
import ConfirmDeleteDialog from './components/ConfirmDeleteDialog';


const formValidationSchema: yup.Schema<IFormData> = yup.object().shape({
  email: yup.string().required('O campo email é obrigatório.').email('Email inválido.'),
  name: yup.string().required('O campo name é obrigatório.').min(3, 'O nome deve ter pelo menos 3 caracteres.'),
  //password: yup.string().required('O campo password é obrigatório.').min(3, 'A senha deve ter pelo menos 3 caracteres.'),
  cpf: yup.string().required('O campo CPF é obrigatório.').min(11, 'O CPF deve ter pelo menos 11 caracteres.'),
});




export const DetalheDeUsuarios: React.FC = () => {
  const { formRef, save, saveAndClose, isSaveAndClose } = useVForm();
  const { id = 'nova' } = useParams<'id'>();
  const navigate = useNavigate();
  const { getUser } = useAuth();


  const [isLoading, setIsLoading] = useState(false);
  const [nome, setNome] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');


  useEffect(() => {
    if (id !== 'nova') {
      setIsLoading(true);

      UsuariosServiceAPI.getById(getUser(), id)
        .then((result) => {
          setIsLoading(false);

          if (result instanceof Error) {
            setSnackbarMessage(result.message);
            navigate('/usuarios');
          } else {
            setNome(result.data.name);

            // Mapear apenas os IDs das roles
            const rolesIds = result.data.roles?.map((role: IRole) => role.id);

            // Setar os dados no formulário incluindo apenas os IDs das roles
            formRef.current?.setData({ ...result.data, roles: rolesIds });
          }
        });
    } else {
      formRef.current?.setData({
        email: '',
        nomeCompleto: '',
        roles: undefined,
      });
    }
  }, [id, getUser, navigate]);



  const handleSave = (dados: IFormData) => {
    console.log(dados)
    formValidationSchema.
      validate(dados, { abortEarly: false })
      .then((dadosValidados) => {
        setIsLoading(true);

        if (id === 'nova') {
          UsuariosServiceAPI
            .create(getUser(), dadosValidados)
            .then((result) => {
              console.log(result.data);
              setIsLoading(false);

              if (result instanceof Error) {
                setSnackbarMessage(result.message);
              } else {
                if (isSaveAndClose()) {
                  navigate('/usuarios');
                } else {
                  navigate(`/usuarios/detalhe/${result.data.accessToken}`);
                }
              }
            });
        } else {

          UsuariosServiceAPI
            .updateById(getUser(), id, { ...dadosValidados })
            .then((result) => {
              setIsLoading(false);

              if (result instanceof Error) {
                setSnackbarMessage(result.message);
              } else {
                if (isSaveAndClose()) {
                  navigate('/usuarios');
                }
              }
            });
        }
      })
      .catch((errors: yup.ValidationError) => {
        const validationErrors: IVFormErrors = {};

        errors.inner.forEach(error => {
          if (!error.path) return;

          validationErrors[error.path] = error.message;
        });

        formRef.current?.setErrors(validationErrors);
      });
  };

  const handleDelete = () => {
    setOpenDialog(true); // Abre o diálogo de confirmação
  };

  const handleConfirmDelete  = () => {
    //if (confirm('Realmente deseja apagar?')) {
      UsuariosServiceAPI.deleteById(getUser(), id)
        .then(result => {
          if (result instanceof Error) {
            setSnackbarMessage(result.message);
          } else {
            setSnackbarMessage('Registro apagado com sucesso!');
            navigate('/usuarios');
          }
        });
    //}
  };

  // Função para `onPointerEnterCapture`
  const handlePointerEnterCapture = (event: React.PointerEvent) => {
    console.log("Pointer entered:", event);
  };

  // Função para `onPointerLeaveCapture`
  const handlePointerLeaveCapture = (event: React.PointerEvent) => {
    console.log("Pointer left:", event);
  };

  // Propriedade para `placeholder`
  const placeholderText = "Digite aqui..."; // Substitua por algo útil ou `undefined`


  return (
    <LayoutBaseDePagina
      titulo={id === 'nova' ? 'Nova pessoa' : nome}
      barraDeFerramentas={
        <FerramentasDeDetalhe
          textoBotaoNovo='Nova'
          mostrarBotaoSalvarEFechar
          mostrarBotaoNovo={id !== 'nova'}
          mostrarBotaoApagar={id !== 'nova'}
          mostrarBotaoSalvarCarregando={isLoading}

          aoClicarEmSalvar={save}
          aoClicarEmSalvarEFechar={saveAndClose}
          aoClicarEmVoltar={() => navigate('/usuarios')}
          aoClicarEmApagar={handleDelete}
          aoClicarEmNovo={() => navigate('/usuarios/detalhe/nova')}
        />
      }
    >
      <ConfirmDeleteDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onConfirm={handleConfirmDelete}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
      <VForm ref={formRef} onSubmit={handleSave} placeholder={placeholderText} onPointerEnterCapture={handlePointerEnterCapture} onPointerLeaveCapture={handlePointerLeaveCapture}>
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined">

          <Grid container direction="column" padding={2} spacing={2} width="100%">

            {isLoading && (
              <Grid item>
                <LinearProgress variant='indeterminate' />
              </Grid>
            )}

            <Grid item>
              <Typography variant='h6'>Conta</Typography>
            </Grid>

            {/* Nome completo ocupando toda a linha */}
            <Grid item xs={12}>
              <VTextField
                fullWidth
                name='name'
                disabled={isLoading}
                label='Nome completo'
                onChange={e => setNome(e.target.value)}
              />
            </Grid>

            {/* Email e CPF na mesma linha */}
            <Grid container item spacing={2}>
              <Grid item xs={6}>
                <VTextField
                  fullWidth
                  name='email'
                  label='Email'
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={6}>
                <VTextField
                  fullWidth
                  name='cpf'
                  disabled={isLoading}
                  label='CPF'
                />
              </Grid>
            </Grid>

            {/* Outros campos em linhas separadas */}
            <Grid container item spacing={2}>
              {id === 'nova' && (  // Adiciona condição para exibir o campo de senha apenas quando for "nova"
                <Grid item xs={6}>
                  <VTextField
                    fullWidth
                    name='password'
                    label='Senha'
                    disabled={isLoading}
                  />
                </Grid>
              )}
            </Grid>
            <RoleCheckbox isExternalLoading={isLoading} />

          </Grid>

        </Box>
      </VForm>
    </LayoutBaseDePagina>
  );
};
